import {defineStore} from "pinia";
import ChatMessage from "@src/types/ChatMessage";
import {nextTick, ref} from "vue";
import {useChatSessionStore} from "@src/stores/chatSession";
import Contact from "@src/types/Contact";
import {Toast} from "@src/plugins/toast";
import MessageService from "@src/services/message";
import useStore from "@src/stores/store";
import GroupMessage from "@src/types/GroupMessage";
import {useAuthStore} from "@src/stores/auth";
import TemplateMessage from "@src/types/TemplateMessage";


export const useMessageStore = defineStore('messages', () => {
    const contact = ref<Contact>()
    const messageTemplatesModal = ref(false)
    const message = ref<string>('')
    const groupMessages = ref<GroupMessage[]>([])
    const groupHasMoreMessages = ref<boolean>(false)
    const groupOldestId = ref<number | null>(null)
    const groupLastId = ref<number | null>(null);
    const templateSearch = ref<string>('')
    const selectedTemplate = ref<number | null>(null)
    const filteredTemplates = ref<TemplateMessage[]>([])
    //bot
    const botMessages = ref<ChatMessage[]>([]);
    const botHasMoreMessages = ref<boolean>(false);
    const botOldestId = ref<number | null>(null);
    const botLastId = ref<number | null>(null);
    const emojiSearch = ref<string>('')


    function openOptionsModal(option: string) {
        const sessionStore = useChatSessionStore()
        sessionStore.openOptionsSelector = false
        if (option === 'templates') {
            messageTemplatesModal.value = true
        }
    }

    async function submitMedia(files: File[], type: string, ext: string[]) {
        const fileSizeLimit = ref<number>(16777216)
        const store = useStore();
        const authStore = useAuthStore()
        const sessionStore = useChatSessionStore()
        for (const file of files) {
            let forbidden = false
            const fileExt = file.name.split('.').pop()?.toLowerCase()

            if (type === 'image' && sessionStore.type !== 'group' && sessionStore.selectedSession?.channel?.type === 'waapi') {
                fileSizeLimit.value = 5242880
            }

            if (file.size > fileSizeLimit.value) {
                Toast.fire(
                    sessionStore.selectedSession?.channel?.type === 'waapi' && type === 'image'
                        ? "Você precisa selecionar um arquivo com no máximo 5MB!"
                        : "Você precisa selecionar um arquivo com no máximo 16MB!",
                    "",
                    "error"
                )
                continue
            }

            if (!ext.includes(fileExt!)) {
                if (type !== 'document') {
                    Toast.fire("Tipo de arquivo inválido!", "", "error")
                    continue
                } else {
                    forbidden = true
                }
            }

            const tempId = -Date.now()
            const localUrl = URL.createObjectURL(file)
            const data = ref<ChatMessage | undefined>({
                id: tempId,
                chat_session_id: sessionStore.selectedSession?.id, // FK
                user_id: store.user?.id,
                human_date: new Date().toISOString(),
                from: 'user',
                type: type,
                has_media: true,
                is_forwarded: false,
                is_status: false,
                is_starred: false,
                is_edited: false,
                broadcast: false,
                reply_to: sessionStore.replyMessage?.id || undefined,
                selected: false,
                message: localUrl,
                status: '0',
                sender: {
                    id: authStore.user?.id ?? 0,
                    name: authStore.user?.name ?? 'Usuário',
                    avatar: authStore.user?.profile_photo_path ?? (authStore.user?.profile_photo_url || ''),
                    type: 'user'
                },
                reactions: [],
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                progress: 1,
                content: ''
            });

            if (sessionStore.type === 'group') {
                const groupMsg: GroupMessage = {
                    id: data.value?.id ?? 0,
                    group_id: (sessionStore.selectedSession as any).id,
                    user_id: store.user?.id ?? null,
                    costumer_id: null,
                    token: '',
                    type: data.value!.type,
                    content: data.value!.message ?? '',
                    reply_to: data.value!.reply_to ? Number(data.value!.reply_to) : null,
                    created_at: data.value!.created_at,
                    updated_at: data.value!.updated_at,
                    selected: false,
                    sender: data.value!.sender,
                };
                groupMessages.value.push(groupMsg);
            } else {
                if (data.value) sessionStore.selectedSession?.messages.push(data.value)
            }

            const fd = new FormData()
            fd.append('file', file)
            fd.append('id', sessionStore.selectedSession?.id?.toString() ?? '')
            fd.append('quoted', sessionStore.replyMessage?.id?.toString() ?? '')
            fd.append('tempId', tempId.toString())
            fd.append('type', type)
            fd.append('sessionType', sessionStore.type ?? '')

            sessionStore.replyMessage = undefined

            await MessageService.sendMedia(fd, forbidden)

            if (sessionStore.type === 'group') {
                const tempIndex = groupMessages.value.findIndex(msg => msg.id === tempId);
                if (tempIndex !== -1 && tempIndex !== undefined) {
                    if (tempIndex !== -1) {
                        groupMessages.value.splice(tempIndex, 1);
                    }
                }
            } else {
                sessionStore.replaceTemporaryMessage(tempId)
            }

            await nextTick()
            await sessionStore.scrollDown()
        }
    }

    function getLastMessagePreview(message: ChatMessage) {
        if (!message) {
            return 'Nenhuma mensagem enviada.';
        }

        if(message.from === 'system') {
            return 'Mensagem do sistema.';
        }

        let fromYou = message.from === 'user'
        const typeMap: Record<string, string> = {
            '': 'Enviou uma mensagem',
            'image': 'Enviou uma imagem',
            'video': 'Enviou um video',
            'document': 'Enviou um documento',
            'vcard': 'Enviou um contato',
            'contact': 'Enviou um contato',
            'sticker': 'Enviou uma figurinha',
            'location': 'Enviou uma localização',
            'audio': 'Enviou um áudio',
            'warning': 'Enviou um comentário'
        }

        return fromYou ? message.sender?.name + ': ' + typeMap[message.type ?? ''] || 'Enviou uma mensagem' : typeMap[message.type ?? ''] || 'Enviou uma mensagem'
    }

    //limpar estado do grupo
    function clearGroupState() {
        groupMessages.value = []
        groupHasMoreMessages.value = false
        groupOldestId.value = null
    }

    function clearBotState() {
        botMessages.value = [];
        botHasMoreMessages.value = false;
        botOldestId.value = null;
        botLastId.value = null;
    }

    return {
        contact,
        openOptionsModal,
        templateSearch,
        messageTemplatesModal,
        message,
        submitMedia,
        getLastMessagePreview,
        groupMessages,
        groupHasMoreMessages,
        groupOldestId,
        groupLastId,
        clearGroupState,
        botMessages,
        botHasMoreMessages,
        botOldestId,
        botLastId,
        clearBotState,
        selectedTemplate,
        filteredTemplates,
        emojiSearch
    }
})